/**
 * Experience Mapper Utility
 * Utilities for mapping API experiences to ExperienceCard format
 */

import { ChatExperience } from '@/types/chatTypes';
import { ExperienceCard } from '@/constants/chatScreenData';

/**
 * Map experienceType string to ExperienceCard type
 */
export const mapExperienceType = (
  expType: string | undefined
): ExperienceCard['type'] => {
  if (!expType) return 'activities'; // Default fallback
  const normalized = expType.toLowerCase();

  // Handle exact matches first
  if (normalized === 'package') return 'package';
  if (normalized === 'guidedactivity' || normalized === 'activity')
    return 'activities';
  if (normalized === 'riderscorner' || normalized === 'riders-corner')
    return 'riders-corner';
  if (normalized === 'signature') return 'signature';
  if (
    normalized === 'stay' ||
    normalized === 'stays' ||
    normalized === 'stayexperience'
  )
    return 'stays';

  // Handle partial matches
  if (normalized.includes('package')) return 'package';
  if (normalized.includes('activity') || normalized.includes('guided'))
    return 'activities';
  if (normalized.includes('rider') || normalized.includes('corner'))
    return 'riders-corner';
  if (normalized.includes('signature')) return 'signature';
  if (normalized.includes('stay') || normalized.includes('hotel'))
    return 'stays';

  return 'activities'; // Default fallback
};

/**
 * Map a single ChatExperience to ExperienceCard format
 */
export const mapChatExperienceToCard = (
  exp: ChatExperience
): ExperienceCard => {
  // Extract images from media array first (for stay experiences), then fallback to imageUrl/image
  let imageArray: string[] = [];

  // Check media array first (for stay experiences)
  if (exp.media && Array.isArray(exp.media)) {
    const mediaImages = exp.media
      .filter(
        item => item.format === 'image' && item.isActive !== false // Default to true if not specified
      )
      .map(item => item.url)
      .filter(
        (url): url is string => typeof url === 'string' && url.length > 0
      );

    if (mediaImages.length > 0) {
      imageArray = mediaImages;
    }
  }

  // Fallback to imageUrl or image if media array didn't yield results
  if (imageArray.length === 0) {
    // Ensure we only use arrays of strings
    const normalizeImageArray = (value: unknown): string[] => {
      if (!value) return [];
      if (Array.isArray(value)) {
        // Filter to only include valid string URLs
        return value.filter(
          (item): item is string => typeof item === 'string' && item.length > 0
        );
      }
      // If it's a single string, wrap it in an array
      if (typeof value === 'string' && value.length > 0) {
        return [value];
      }
      return [];
    };

    const imageUrlArray = normalizeImageArray(exp.imageUrl);
    const imageArrayFromImage = normalizeImageArray(exp.image);

    imageArray = imageUrlArray.length > 0 ? imageUrlArray : imageArrayFromImage;
  }

  // Ensure primaryImage is always a string
  const primaryImage =
    imageArray.length > 0 && typeof imageArray[0] === 'string'
      ? imageArray[0]
      : 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop&q=80';

  // Handle ID - prefer id, fallback to _id
  const experienceId = exp.id || exp._id || '';

  // Handle rating - prefer averageRating/reviewCount (new), fallback to rating/ratingCount (old)
  const rating = exp.averageRating ?? exp.rating;
  const ratingCount = exp.reviewCount ?? exp.ratingCount ?? exp.total_ratings;

  // Handle price - check pricing.pricePerPerson first (for stay experiences), then fallback to price
  let price: string | undefined = undefined;

  // Check pricing.pricePerPerson first (for stay experiences)
  if (
    exp.pricing?.pricePerPerson !== undefined &&
    exp.pricing.pricePerPerson !== null
  ) {
    const pricePerPerson = exp.pricing.pricePerPerson;
    if (typeof pricePerPerson === 'number' && pricePerPerson > 0) {
      price = pricePerPerson.toString();
    } else if (typeof pricePerPerson === 'string') {
      const parsed = parseFloat(pricePerPerson);
      if (!isNaN(parsed) && parsed > 0) {
        price = parsed.toString();
      }
    }
  }

  // Fallback to exp.price if pricing.pricePerPerson wasn't available
  if (!price && exp.price !== undefined && exp.price !== null) {
    const priceValue = exp.price;
    if (typeof priceValue === 'number' && priceValue > 0) {
      price = priceValue.toString();
    } else if (typeof priceValue === 'string') {
      const parsed = parseFloat(priceValue);
      if (!isNaN(parsed) && parsed > 0) {
        price = parsed.toString();
      }
    }
  }

  // Handle duration - format activities in hours/minutes, others as-is
  // Note: exp.hours is the total duration (summation of all activities' durations) in minutes
  const getDuration = (): string | undefined => {
    const expType = mapExperienceType(exp.experienceType);

    // For activities, format total duration (sum of all activities) in hours/minutes like experiences screen
    if (expType === 'activities') {
      if (typeof exp.hours === 'number') {
        // exp.hours is in minutes (total duration = sum of all activities)
        // Format as hours/minutes (e.g., "2 hrs 30 mins" or "45 mins")
        if (exp.hours >= 60) {
          const hours = Math.floor(exp.hours / 60);
          const minutes = exp.hours % 60;
          return `${hours} hr${hours > 1 ? 's' : ''}${
            minutes !== 0 ? ` ${minutes} min${minutes > 1 ? 's' : ''}` : ''
          }`;
        }
        return `${exp.hours} min${exp.hours > 1 ? 's' : ''}`;
      }
      // Fallback to duration string if hours not available
      return exp.duration;
    }

    // For other types (packages, stays, etc.), use duration as-is or convert hours to string
    return (
      exp.duration ||
      (exp.hours
        ? `${exp.hours} ${exp.hours === 1 ? 'Hour' : 'Hours'}`
        : undefined)
    );
  };

  const duration = getDuration();

  // Handle tags - extract from tags array, categories array, or category
  const getTags = (): string[] => {
    const tags: string[] = [];
    const expRecord = exp as unknown as Record<string, unknown>;

    // If tags are already provided, use them
    if (exp.tags && Array.isArray(exp.tags) && exp.tags.length > 0) {
      return exp.tags.filter((tag): tag is string => typeof tag === 'string');
    }

    // Add main category
    if (exp.category?.title) {
      tags.push(exp.category.title);
    }

    // Add additional categories from categories array (if API returns it)
    if (expRecord.categories && Array.isArray(expRecord.categories)) {
      expRecord.categories.forEach((cat: unknown) => {
        let catName: string | undefined;
        if (typeof cat === 'string') {
          catName = cat;
        } else if (typeof cat === 'object' && cat !== null) {
          const catObj = cat as Record<string, unknown>;
          // Check multiple possible fields: title, name, description
          catName =
            (typeof catObj.title === 'string' ? catObj.title.trim() : '') ||
            (typeof catObj.name === 'string' ? catObj.name.trim() : '') ||
            (typeof catObj.description === 'string'
              ? catObj.description.trim()
              : '') ||
            undefined;
        }
        if (catName && catName.length > 0 && !tags.includes(catName)) {
          tags.push(catName);
        }
      });
    }

    return tags;
  };

  const tags = getTags();

  // Extract coordinates from various possible locations
  let latitude: number | undefined;
  let longitude: number | undefined;

  // Check direct coordinates first
  if (exp.latitude && exp.longitude) {
    latitude = exp.latitude;
    longitude = exp.longitude;
  } else {
    // Check locationId.coordinates.coordinates or location.coordinates.coordinates (GeoJSON format: [longitude, latitude])
    const location = exp.locationId || exp.location;

    if (
      location?.coordinates?.coordinates &&
      Array.isArray(location.coordinates.coordinates) &&
      location.coordinates.coordinates.length >= 2
    ) {
      const [lng, lat] = location.coordinates.coordinates;
      if (
        lng != null &&
        lat != null &&
        !isNaN(lng) &&
        !isNaN(lat) &&
        typeof lng === 'number' &&
        typeof lat === 'number'
      ) {
        longitude = lng;
        latitude = lat;
      }
    }
  }

  return {
    id: experienceId,
    title: exp.title || exp.name || 'Untitled Experience',
    image: primaryImage,
    images: imageArray.length > 0 ? imageArray : undefined,
    location: exp.place || 'Location not specified',
    type: mapExperienceType(exp.experienceType),
    slug: exp.slug || '',
    // Include optional fields if available from API
    rating,
    ratingCount,
    description: exp.description || exp.shortDescription,
    price,
    currencySymbol: exp.pricing?.currencySymbol || exp.currencySymbol,
    duration,
    attractionsCount:
      typeof exp.attractionsCount === 'number'
        ? exp.attractionsCount
        : Array.isArray(exp.attractions)
          ? exp.attractions.length
          : 0,
    days: exp.days,
    nights: exp.nights,
    locationsCount: exp.locationsCount || exp.locations,
    tags,
    // Include coordinates if available
    latitude,
    longitude,
  };
};

/**
 * Filter and map an array of ChatExperiences to ExperienceCard format
 */
export const mapChatExperiencesToCards = (
  experiences: ChatExperience[] | undefined | null
): ExperienceCard[] => {
  if (!experiences || experiences.length === 0) {
    return [];
  }

  return experiences
    .filter(exp => {
      // Filter out invalid experiences
      if (!exp) return false;
      if (!exp.title && !exp.name && !exp._id && !exp.id) return false;
      return true;
    })
    .map(mapChatExperienceToCard);
};
